import React, { useState } from "react";
import {
  useConsultantRequests,
  useConsultantRequestStats,
  useUpdateConsultantRequestStatus,
  useConvertConsultantRequest,
} from "../hooks/useConsultantRequests";
import { useLocalStorage } from "../hooks/useLocalStorage";
import SearchBar from "../components/common/SearchBar";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Pagination from "../components/common/Pagination";
import { ConsultantRequest, ConsultantRequestStatus } from "../services/api";
import {
  FunnelIcon,
  PhoneIcon,
  EnvelopeIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  EyeIcon,
  BoltIcon,
} from "@heroicons/react/24/outline";

type FilterStatus = "all" | ConsultantRequestStatus;

const ConsultantRequests: React.FC = () => {
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] =
    useState<ConsultantRequest | null>(null);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isConvertModalOpen, setIsConvertModalOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<ConsultantRequestStatus>("pending");
  const [adminNotes, setAdminNotes] = useState("");
  const [contractNumber, setContractNumber] = useState("");
  const [contractPartner, setContractPartner] = useState("");
  const [filter, setFilter] = useLocalStorage<FilterStatus>(
    "consultant-requests-filter",
    "all",
  );
  const [searchQuery, setSearchQuery] = useLocalStorage<string>(
    "consultant-requests-search-query",
    "",
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(20);

  const { data: requestsResponse, isLoading } = useConsultantRequests({
    limit: itemsPerPage,
    page: currentPage,
    search: searchQuery || undefined,
    status: filter !== "all" ? filter : undefined,
  });

  const { data: stats } = useConsultantRequestStats();
  const updateStatus = useUpdateConsultantRequestStatus();
  const convertRequest = useConvertConsultantRequest();

  const requests = requestsResponse?.data || [];
  const totalItems = requestsResponse?.total || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Reset page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchQuery]);

  const handleViewDetails = (request: ConsultantRequest) => {
    setSelectedRequest(request);
    setIsDetailModalOpen(true);
  };

  const handleOpenStatusModal = (request: ConsultantRequest) => {
    setSelectedRequest(request);
    setNewStatus(request.status);
    setAdminNotes(request.adminNotes || "");
    setIsStatusModalOpen(true);
  };

  const handleOpenConvertModal = (request: ConsultantRequest) => {
    setSelectedRequest(request);
    setContractNumber("");
    setContractPartner("");
    setIsConvertModalOpen(true);
  };

  const handleUpdateStatus = () => {
    if (!selectedRequest) return;
    updateStatus.mutate(
      {
        requestId: selectedRequest.requestId,
        status: newStatus,
        adminNotes: adminNotes || undefined,
      },
      { onSuccess: () => setIsStatusModalOpen(false) },
    );
  };

  const handleConvert = () => {
    if (!selectedRequest || !contractNumber.trim() || !contractPartner.trim()) return;
    convertRequest.mutate(
      {
        requestId: selectedRequest.requestId,
        contractData: {
          energyContractNumber: contractNumber,
          energyContractPartner: contractPartner,
        },
      },
      { onSuccess: () => setIsConvertModalOpen(false) },
    );
  };

  const getStatusBadge = (status: ConsultantRequestStatus) => {
    const badges: Record<
      ConsultantRequestStatus,
      { bg: string; icon: React.ReactNode; label: string }
    > = {
      pending: {
        bg: "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200",
        icon: <ClockIcon className="h-4 w-4 mr-1" />,
        label: "Pending",
      },
      contacted: {
        bg: "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200",
        icon: <PhoneIcon className="h-4 w-4 mr-1" />,
        label: "Contacted",
      },
      converted: {
        bg: "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200",
        icon: <CheckCircleIcon className="h-4 w-4 mr-1" />,
        label: "Converted",
      },
      rejected: {
        bg: "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200",
        icon: <XCircleIcon className="h-4 w-4 mr-1" />,
        label: "Rejected",
      },
    };
    const badge = badges[status];
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg}`}
      >
        {badge.icon}
        {badge.label}
      </span>
    );
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Consultant Requests
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage energy consultant contact requests
          </p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Total
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.total || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">Pending</div>
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
              {stats.pending || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Contacted
            </div>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {stats.contacted || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Converted
            </div>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {stats.converted || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Rejected
            </div>
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {stats.rejected || 0}
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0 lg:space-x-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <SearchBar
              onSearch={setSearchQuery}
              initialValue={searchQuery}
              placeholder="Search by name, email or city..."
            />
          </div>

          {/* Filters */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as FilterStatus)}
                className="rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="contacted">Contacted</option>
                <option value="converted">Converted</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Requests Table */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      ) : requests.length > 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Contact Info
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Comment
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {requests.map((request) => (
                  <tr
                    key={request._id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm font-mono text-gray-900 dark:text-white">
                        {request.requestId}
                      </span>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {request.customerDisplayId}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {request.customerSnapshot.firstName}{" "}
                          {request.customerSnapshot.lastName}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {request.customerSnapshot.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <div className="flex items-center text-sm text-gray-900 dark:text-white">
                          <EnvelopeIcon className="h-4 w-4 mr-1 text-gray-400" />
                          {request.contactEmail}
                        </div>
                        <div className="flex items-center text-sm text-gray-900 dark:text-white">
                          <PhoneIcon className="h-4 w-4 mr-1 text-gray-400" />
                          {request.contactPhone}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
                        {request.comment}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {getStatusBadge(request.status)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(request.createdAt)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewDetails(request)}
                          className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300"
                          title="View Details"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleOpenStatusModal(request)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                          title="Update Status"
                        >
                          <ArrowPathIcon className="h-5 w-5" />
                        </button>
                        {(request.status === "pending" || request.status === "contacted") && (
                          <button
                            onClick={() => handleOpenConvertModal(request)}
                            className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300"
                            title="Convert to Contract"
                          >
                            <BoltIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
            />
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center border border-gray-200 dark:border-gray-700">
          <div className="text-gray-400 dark:text-gray-500 mb-3">
            <ChatBubbleLeftRightIcon className="h-12 w-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No consultant requests found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {searchQuery || filter !== "all"
              ? "Try adjusting your search or filters"
              : "No consultant requests yet"}
          </p>
        </div>
      )}

      {/* Detail Modal */}
      {selectedRequest && isDetailModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              onClick={() => setIsDetailModalOpen(false)}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-primary-600 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <ChatBubbleLeftRightIcon className="h-8 w-8 text-white" />
                  <div>
                    <h3 className="text-xl font-semibold text-white">
                      Consultant Request Details
                    </h3>
                    <p className="text-sm text-primary-100">
                      {selectedRequest.requestId}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsDetailModalOpen(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="bg-white dark:bg-gray-800 px-6 py-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                <div className="space-y-6">
                  {/* Status */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Status
                    </span>
                    {getStatusBadge(selectedRequest.status)}
                  </div>

                  {/* Customer Info */}
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                      <UserIcon className="h-5 w-5 mr-2" />
                      Customer Information
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Customer ID
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">
                          {selectedRequest.customerDisplayId}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Name
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {selectedRequest.customerSnapshot.firstName}{" "}
                          {selectedRequest.customerSnapshot.lastName}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Email
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white break-all">
                          {selectedRequest.customerSnapshot.email}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Current Points
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {selectedRequest.customerSnapshot.currentPoints?.toLocaleString() || 0} EP
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                      <PhoneIcon className="h-5 w-5 mr-2" />
                      Contact Request
                    </h4>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Contact Email
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {selectedRequest.contactEmail}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Contact Phone
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {selectedRequest.contactPhone}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Comment / Preferences
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                          {selectedRequest.comment}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Backoffice Info */}
                  {selectedRequest.backofficeResponse && (
                    <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                        Backoffice Lead
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Lead ID
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">
                            {selectedRequest.backofficeLeadId || "-"}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Status
                          </label>
                          <p className="mt-1 text-sm">
                            {selectedRequest.backofficeResponse.success ? (
                              <span className="text-green-600 dark:text-green-400">Sent Successfully</span>
                            ) : (
                              <span className="text-red-600 dark:text-red-400">Failed to Send</span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Admin Notes */}
                  {selectedRequest.adminNotes && (
                    <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                        Admin Notes
                      </h4>
                      <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                        {selectedRequest.adminNotes}
                      </p>
                    </div>
                  )}

                  {/* Timestamps */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Timestamps
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Created
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {formatDate(selectedRequest.createdAt)}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Updated
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {formatDate(selectedRequest.updatedAt)}
                        </p>
                      </div>
                      {selectedRequest.processedAt && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Processed
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {formatDate(selectedRequest.processedAt.toString())}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 flex justify-end space-x-3">
                <button
                  onClick={() => setIsDetailModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setIsDetailModalOpen(false);
                    handleOpenStatusModal(selectedRequest);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                >
                  Update Status
                </button>
                {(selectedRequest.status === "pending" || selectedRequest.status === "contacted") && (
                  <button
                    onClick={() => {
                      setIsDetailModalOpen(false);
                      handleOpenConvertModal(selectedRequest);
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
                  >
                    Convert to Contract
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {isStatusModalOpen && selectedRequest && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              onClick={() => setIsStatusModalOpen(false)}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-blue-600 px-6 py-4">
                <h3 className="text-xl font-semibold text-white">
                  Update Status
                </h3>
                <p className="text-sm text-blue-100">
                  {selectedRequest.requestId}
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800 px-6 py-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Status
                  </label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value as ConsultantRequestStatus)}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
                  >
                    <option value="pending">Pending</option>
                    <option value="contacted">Contacted</option>
                    <option value="converted">Converted</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Admin Notes (Optional)
                  </label>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    rows={4}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Add notes about this request..."
                  />
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 flex justify-end space-x-3">
                <button
                  onClick={() => setIsStatusModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateStatus}
                  disabled={updateStatus.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updateStatus.isPending ? "Updating..." : "Update Status"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Convert Modal */}
      {isConvertModalOpen && selectedRequest && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              onClick={() => setIsConvertModalOpen(false)}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-green-600 px-6 py-4">
                <h3 className="text-xl font-semibold text-white">
                  Convert to Energy Contract
                </h3>
                <p className="text-sm text-green-100">
                  Customer: {selectedRequest.customerSnapshot.firstName} {selectedRequest.customerSnapshot.lastName}
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800 px-6 py-6 space-y-4">
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    This will update the customer's energy contract information and mark this request as converted.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Energy Contract Partner *
                  </label>
                  <input
                    type="text"
                    value={contractPartner}
                    onChange={(e) => setContractPartner(e.target.value)}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
                    placeholder="e.g., E.ON, Vattenfall, RWE..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Energy Contract Number *
                  </label>
                  <input
                    type="text"
                    value={contractNumber}
                    onChange={(e) => setContractNumber(e.target.value)}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
                    placeholder="Enter contract number..."
                  />
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 flex justify-end space-x-3">
                <button
                  onClick={() => setIsConvertModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConvert}
                  disabled={!contractNumber.trim() || !contractPartner.trim() || convertRequest.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {convertRequest.isPending ? "Converting..." : "Convert to Contract"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultantRequests;
